import {
  chatReferenceHref,
  readChatReference,
  readChatObjects,
  readChatHelp,
  readChatOperations,
  type ChatItemReference,
  type ChatObject,
  type ChatHelp,
  type ChatOperation,
  type ChatOperationRequest,
  type ChatRequestContext,
} from "./chat-contracts";
export * from "./chat-contracts";
export type ChatSource = {
  citation_id: string;
  stasht_id: string;
  title: string;
  context?: ChatItemReference["context"];
};
export const chatSourceReference = (source: ChatSource): ChatItemReference => ({
  kind: "stash",
  id: source.stasht_id,
  ...(source.context ? { context: source.context } : {}),
});
export type ChatImage = {
  image_id: string;
  title: string;
  image_url: string;
  thumbnail_url?: string;
  page_url: string;
  source: string;
  is_sensitive?: boolean;
};
export type ChatAnswer = {
  answer: string;
  cards: (ChatSource & { why?: string })[];
  follow_ups: string[];
  object_ids?: string[];
  actions?: string[];
};
export type ChatTurn = {
  cloud?: { revision: number; signature: string; running?: boolean };
  id: string;
  query: string;
  createdAt: number;
  state: "answering" | "complete" | "stopped" | "interrupted" | "failed";
  text: string;
  sources: ChatSource[];
  images: ChatImage[];
  answer?: ChatAnswer;
  error?: string;
  textSettled?: boolean;
  progressMessage?: string;
  objects?: ChatObject[];
  help?: ChatHelp;
  operations?: ChatOperation[];
  requestID?: string;
  requestStartedAt?: number;
  selectedReferences?: ChatItemReference[];
  operationRequest?: ChatOperationRequest;
  operationTargetID?: string;
  openerReferences?: ChatItemReference[];
  openerAsk?: string;
};
/** Request recovery may continue previously authorized work; status only reads. */
export function chatRecoveryKind(turn: ChatTurn): "status" | "recover" {
  return turn.operationRequest && ["apply", "resume", "status", "cancel"].includes(turn.operationRequest.action)
    ? "status"
    : "recover";
}
export type ChatConversation = {
  cloud?: { revision: number; contentRevision: number; title: string };
  id: string;
  title: string;
  titleWasEdited?: boolean;
  createdAt: number;
  updatedAt: number;
  draft: string;
  draftSelection?: ChatItemReference[];
  turns: ChatTurn[];
};
export type ChatArchive = {
  version: 1;
  generation?: string;
  legacyImportedIds?: string[];
  conversations: ChatConversation[];
  deletions?: string[];
};
export type ChatEvent = { type: string; data: unknown };
export class ChatArchiveConflict extends Error {
  constructor() {
    super("This chat changed in another tab. Your unsaved text is still here; copy it before reloading the page.");
  }
}
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
const strings = (value: unknown) =>
  Array.isArray(value) ? value.filter((s): s is string => typeof s === "string") : [];
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
export function chatConversationUnchanged(a: ChatConversation, b: ChatConversation | undefined) {
  const normalized = (value: ChatConversation | undefined) =>
    value && {
      ...value,
      turns: value.turns.map(turn => ({ ...turn, state: turn.state === "answering" ? "interrupted" : turn.state })),
    };
  return same(normalized(a), normalized(b));
}

/** Lists of turns accumulate. Editing an older tab's draft or title must not
 * replace a newer reply written in another tab. Only local changes are applied. */
export function mergeChatConversation(
  stored: ChatConversation | undefined,
  base: ChatConversation | undefined,
  next: ChatConversation,
): ChatConversation {
  if (!stored) {
    if (base) throw new ChatArchiveConflict();
    return next;
  }
  if (!base) return next;
  const before = new Map(base.turns.map(turn => [turn.id, turn]));
  const edited = new Map(next.turns.map(turn => [turn.id, turn]));
  const turns = new Map(stored.turns.map(turn => [turn.id, turn]));
  for (const [id, turn] of before) if (!edited.has(id) && same(turns.get(id), turn)) turns.delete(id);
  for (const turn of next.turns) if (!same(turn, before.get(turn.id))) turns.set(turn.id, turn);
  return {
    ...stored,
    ...(!same(next.cloud, base.cloud) ? { cloud: next.cloud } : {}),
    ...(next.title !== base.title ? { title: next.title } : {}),
    ...(next.titleWasEdited !== base.titleWasEdited ? { titleWasEdited: next.titleWasEdited } : {}),
    ...(next.draft !== base.draft ? { draft: next.draft } : {}),
    ...(!same(next.draftSelection, base.draftSelection) ? { draftSelection: next.draftSelection } : {}),
    updatedAt: Math.max(stored.updatedAt, next.updatedAt),
    turns: [...turns.values()].sort((a, b) => a.createdAt - b.createdAt),
  };
}
export function chatRequestContext(
  now = new Date(),
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  libraryActions = false,
): ChatRequestContext {
  const fields = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" })
      .formatToParts(now)
      .map(part => [part.type, part.value]),
  );
  const hour = Number(new Intl.DateTimeFormat("en-US", { timeZone, hour: "2-digit", hourCycle: "h23" }).format(now));
  // The deployed product_help flag selects native iOS labels/offers. Web and
  // the existing Capacitor shell still receive help, using their own labels.
  return {
    schema_version: "stasht.ask_context.v1",
    capabilities: ["inline_media", "library_results", ...(libraryActions ? ["library_actions"] : [])],
    request_date: `${fields.year}-${fields.month}-${fields.day}`,
    time_zone: timeZone,
    daypart: Math.floor(hour / 4),
  };
}
export function chatFailureMessage(error: unknown) {
  if (error instanceof ChatArchiveConflict) return error.message;
  if (error instanceof Error && ["TimeoutError", "AbortError"].includes(error.name))
    return "The reply was interrupted. You can retry when you’re ready.";
  if (error instanceof TypeError) return "Chat could not connect. Check your connection and try again.";
  if (error instanceof SyntaxError) return "This reply could not be read completely. Please try again.";
  return error instanceof Error ? error.message : "This reply could not finish. Please try again.";
}

export function webUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return;
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password ? url.href : undefined;
  } catch {
    return;
  }
}
export function readChatSources(value: unknown): ChatSource[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(entry => {
    const source = record(entry);
    return typeof source.citation_id === "string" &&
      typeof source.stasht_id === "string" &&
      source.stasht_id &&
      typeof source.title === "string"
      ? [
          {
            citation_id: source.citation_id,
            stasht_id: source.stasht_id,
            title: source.title,
            ...(readChatReference({ kind: "stash", id: source.stasht_id, context: source.context })?.context
              ? {
                  context: readChatReference({ kind: "stash", id: source.stasht_id, context: source.context })!.context,
                }
              : {}),
          },
        ]
      : [];
  });
}
export function readChatImages(value: unknown): ChatImage[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap(entry => {
    const image = record(entry),
      imageUrl = webUrl(image.image_url),
      pageUrl = webUrl(image.page_url);
    return typeof image.image_id === "string" && typeof image.title === "string" && imageUrl && pageUrl
      ? [
          {
            image_id: image.image_id,
            title: image.title,
            image_url: imageUrl,
            page_url: pageUrl,
            thumbnail_url: webUrl(image.thumbnail_url),
            source: typeof image.source === "string" ? image.source : new URL(pageUrl).hostname,
            is_sensitive: image.is_sensitive === true,
          },
        ]
      : [];
  });
}
export function turnSources(turn: ChatTurn): ChatSource[] {
  const seen = new Set<string>();
  return [...turn.sources, ...(turn.answer?.cards || [])].filter(
    source => !seen.has(source.citation_id) && !!seen.add(source.citation_id),
  );
}
export function applyChatEvent(turn: ChatTurn, event: ChatEvent): ChatTurn {
  const data = record(event.data);
  switch (event.type) {
    case "text_delta":
      return { ...turn, text: turn.text + (typeof data.text === "string" ? data.text : ""), textSettled: false };
    case "answer_end":
      return { ...turn, textSettled: true };
    case "progress":
      return { ...turn, progressMessage: typeof data.message === "string" ? data.message : undefined };
    case "objects":
      return { ...turn, objects: readChatObjects(data.objects) };
    case "operations":
      return { ...turn, operations: readChatOperations(data.operations) };
    case "sources":
      return { ...turn, sources: readChatSources(data.sources) };
    case "images":
      return { ...turn, images: readChatImages(data.images) };
    case "error":
      throw new Error("This reply could not finish. Please try again.");
    case "complete": {
      const answer = record(data.answer);
      if (data.schema_version !== "stasht.ask_answer.v1" || typeof answer.answer !== "string" || !answer.answer.trim())
        throw new Error("Chat returned an unreadable reply. Please try again.");
      const cards = Array.isArray(answer.cards)
        ? answer.cards.flatMap(value => {
            const source = readChatSources([value])[0],
              why = record(value).why;
            return source ? [{ ...source, why: typeof why === "string" ? why : undefined }] : [];
          })
        : [];
      const warnings = strings(record(record(data.diagnostics).validation).warnings);
      return {
        ...turn,
        text: answer.answer,
        answer: {
          answer: answer.answer,
          cards,
          follow_ups: strings(answer.follow_ups),
          object_ids: strings(answer.object_ids),
          actions: strings(answer.actions),
        },
        textSettled: !warnings.some(value => ["answer_truncated", "answer_interrupted"].includes(value)),
        objects: Array.isArray(data.objects) ? readChatObjects(data.objects) : turn.objects,
        help: readChatHelp(data.help),
        operations: data.operations === undefined ? turn.operations : readChatOperations(data.operations),
        sources: Array.isArray(data.sources) ? readChatSources(data.sources) : turn.sources,
        images: Array.isArray(data.images) ? readChatImages(data.images) : turn.images,
        state: warnings.some(value => ["answer_truncated", "answer_interrupted"].includes(value))
          ? "interrupted"
          : "complete",
      };
    }
    default:
      return turn;
  }
}

/** Invalid archives are left untouched. They must never become an empty write. */
export function readChatArchive(value: unknown, restoring = true): ChatArchive {
  if (value === undefined) return { version: 1, conversations: [] };
  const archive = record(value);
  if (archive.version !== 1 || !Array.isArray(archive.conversations))
    throw new Error("Saved chats could not be opened. They have been kept on this browser.");
  if (archive.generation !== undefined && typeof archive.generation !== "string")
    throw new Error("Saved chats could not be opened. They have been kept on this browser.");
  if (
    archive.legacyImportedIds !== undefined &&
    (!Array.isArray(archive.legacyImportedIds) || archive.legacyImportedIds.some(id => typeof id !== "string"))
  )
    throw new Error("Saved chats could not be opened. They have been kept on this browser.");
  const conversations = archive.conversations.map(entry => {
    const conversation = record(entry);
    if (
      typeof conversation.id !== "string" ||
      typeof conversation.title !== "string" ||
      typeof conversation.draft !== "string" ||
      !Number.isFinite(conversation.updatedAt) ||
      !Number.isFinite(conversation.createdAt) ||
      !Array.isArray(conversation.turns)
    )
      throw new Error("Saved chats could not be opened. They have been kept on this browser.");
    const turns = conversation.turns.map(entry => {
      const turn = record(entry);
      if (
        typeof turn.id !== "string" ||
        typeof turn.query !== "string" ||
        typeof turn.text !== "string" ||
        !Number.isFinite(turn.createdAt) ||
        !["answering", "complete", "stopped", "interrupted", "failed"].includes(String(turn.state)) ||
        !Array.isArray(turn.sources) ||
        !Array.isArray(turn.images)
      )
        throw new Error("Saved chats could not be opened. They have been kept on this browser.");
      if (turn.answer !== undefined) {
        const answer = record(turn.answer);
        if (typeof answer.answer !== "string" || !Array.isArray(answer.cards) || !Array.isArray(answer.follow_ups))
          throw new Error("Saved chats could not be opened. They have been kept on this browser.");
      }
      return {
        ...turn,
        sources: readChatSources(turn.sources),
        images: readChatImages(turn.images),
        objects: turn.objects === undefined ? undefined : readChatObjects(turn.objects),
        help: readChatHelp(turn.help),
        operations: turn.operations === undefined ? undefined : readChatOperations(turn.operations),
        state: restoring && turn.state === "answering" ? "interrupted" : turn.state,
      } as ChatTurn;
    });
    return { ...conversation, turns } as ChatConversation;
  });
  return {
    version: 1,
    ...(Array.isArray(archive.deletions) ? { deletions: strings(archive.deletions) } : {}),
    ...(typeof archive.generation === "string" ? { generation: archive.generation } : {}),
    ...(Array.isArray(archive.legacyImportedIds) ? { legacyImportedIds: strings(archive.legacyImportedIds) } : {}),
    conversations,
  };
}

/** Old labels are scoped to one answer. Resolve their destinations before using
 * a transcript as context for another answer with its own S1/I1 labels. */
export function chatContextText(turn: ChatTurn): string {
  const destinations = [
    ...turnSources(turn).map(
      source =>
        `${source.citation_id}: ${source.title} — https://stasht.app${chatReferenceHref(chatSourceReference(source))}`,
    ),
    ...turn.images.map(image => `${image.image_id}: ${image.title} — ${image.page_url}`),
    ...(turn.objects || []).map(
      object => `${object.chat_url}: ${object.title} — https://stasht.app${chatReferenceHref(object.ref)}`,
    ),
  ];
  return turn.text + (destinations.length ? "\nReferenced links for this reply:\n" + destinations.join("\n") : "");
}
export function chatHistory(conversation: ChatConversation, excludeId?: string) {
  return conversation.turns
    .filter(turn => turn.id !== excludeId)
    .slice(-10)
    .flatMap(turn => [
      { role: "user" as const, content: turn.query },
      ...(turn.text.trim() ? [{ role: "assistant" as const, content: chatContextText(turn) }] : []),
    ])
    .slice(-10);
}

/** HTTP 200 is not completion. Read the terminal event and reject truncated
 * streams, including a final line without a newline and split UTF-8 chunks. */
export async function consumeChatStream(
  response: Response,
  receive: (event: ChatEvent) => void,
  signal: AbortSignal,
  terminalEvent: "complete" | "openers" = "complete",
) {
  if (!response.ok)
    throw new Error(
      response.status === 403
        ? "Chat is not available for this account. Your saved conversations are still here."
        : response.status === 401
          ? "Your session could not be verified. Sign in again to continue."
          : "Chat could not connect. Please try again.",
    );
  if (!response.body) throw new Error("Chat did not return a reply. Please try again.");
  const reader = response.body.getReader(),
    decoder = new TextDecoder();
  let buffer = "",
    complete = false;
  const line = (value: string) => {
    if (!value.trim()) return;
    const event = record(JSON.parse(value));
    if (typeof event.type !== "string") throw new Error("Chat returned an unreadable reply. Please try again.");
    receive({ type: event.type, data: event.data });
    if (event.type === terminalEvent) complete = true;
  };
  try {
    while (!complete) {
      signal.throwIfAborted();
      const { value, done } = await reader.read();
      buffer += done ? decoder.decode() : decoder.decode(value, { stream: true });
      let index: number;
      while (!complete && (index = buffer.indexOf("\n")) >= 0) {
        line(buffer.slice(0, index));
        buffer = buffer.slice(index + 1);
      }
      if (done) {
        if (!complete && buffer.trim()) line(buffer);
        break;
      }
    }
    if (!complete) throw new Error("The connection ended before the reply finished.");
  } finally {
    await reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}
